import React, { createContext, useContext, useRef, useState } from "react";
import axios from "axios";
import {
    ACCOUNT_TYPE_NORMAL,
    ACCOUNT_TYPE_TEMPORARY,
    API_BASE_URL,
    PRODUCT_TYPE_VOUCHER_MANAGER,
    SITE_DISPLAY_COMPLIMENTARY
} from "@config/config";
import { ApplicationContext } from "@context/ApplicationContext";
import { AuthContext } from "@context/AuthContext";
import { CheckoutComplimentaryContext } from "@context/CheckoutComplimentaryContext";
import { PromoContext } from "@context/PromoContext";
import { getStripeEnabled } from "@utils/whiteLabelConfig";

export const BookSessionContext = createContext({});

const BookSessionContextProvider = (props) => {
    const { productTypeId, whiteLabel } = useContext(ApplicationContext);
    const {
        user, isLoggedIn, setUserAuth, openDrawer, closeDrawer, setPendingActionUrl,
        setStripeClientSecret, setStripeSessionId,
    } = useContext(AuthContext);
    const { checkoutComplementary } = useContext(CheckoutComplimentaryContext);
    const { selectedPromo, standardPromoCode, setAppliedPromo } = useContext(PromoContext);

    const [ticketQuantity, setTicketQuantity] = useState(0);
    const [bookingSessionId, setBookingSessionId] = useState(null);
    const [uuid, setUuid] = useState(null);
    const [checkout, setCheckout] = useState(false);
    const [error, setError] = useState("");
    const [errorList, setErrorList] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [promoCode, setPromoCode] = useState("");
    const [promoCodeSuccess, setPromoCodeSuccess] = useState(null);
    const [referralCode, setReferralCode] = useState("");
    const [referralSuccess, setReferralSuccess] = useState(null);
    const [ewayAccessCode, setEwayAccessCode] = useState(null);
    const [isTempAccount, setIsTempAccount] = useState(false);
    const [ewayFormActionUrl, setEwayFormActionUrl] = useState(null);
    const [promoCodeMessage, setPromoCodeMessage] = useState("");
    const [success, setSuccess] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [errorMessages, setErrorMessages] = useState({});
    const [seasonalPage, setSeasonalPage] = useState(false);
    const [proceedToCheckout, setProceedToCheckout] = useState(false);
    const [isStripeCheckoutLoading, setIsStripeCheckoutLoading] = useState(false);
    const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
    // Bumped on every successful hold so consumers can tell the purchase changed.
    const [holdVersion, setHoldVersion] = useState(0);
    const { children } = props;

    const isVoucherManager = productTypeId === PRODUCT_TYPE_VOUCHER_MANAGER;

    // Snapshot of the request behind the held purchase; tells a re-checkout from a reopen.
    const stripeCartRef = useRef(null);

    // Everything that affects the hold, so any change forces a fresh /ticket call.
    const buildTicketRequest = (bookingType, giftingData, contactDetails) => ({
        items: selectedItems,
        standard_promo_code: standardPromoCode,
        selected_promo_code: selectedPromo?.unique_code ?? "",
        referral_code: referralCode,
        type: bookingType,
        advert_url: window.location.pathname,
        ...(contactDetails?.purchaseEmail && { purchase_email: contactDetails.purchaseEmail }),
        ...(contactDetails?.firstName && { first_name: contactDetails.firstName }),
        ...(contactDetails?.lastName && { last_name: contactDetails.lastName }),
        ...(giftingData?.isGift && {
            is_gift: true,
            gift_recipient_name: giftingData.giftRecipientName,
            gift_recipient_email: giftingData.giftRecipientEmail,
            gift_message: giftingData.giftMessage,
        }),
    });


    const checkoutBookingSession = (totalTicketQuantity, bookingType, giftingData = null, contactDetails = null) => {
        if (!isLoggedIn || user.account_type_id === ACCOUNT_TYPE_TEMPORARY) {
            setIsTempAccount(true);
        }

        const isVoucherCheckout = isVoucherManager || seasonalPage === true;
        const request = buildTicketRequest(bookingType, giftingData, contactDetails);
        const requestSignature = JSON.stringify(request);

        // Request unchanged, vouchers still held: just remount Stripe, skip the re-hold.
        if (
            getStripeEnabled(whiteLabel) && isVoucherCheckout && uuid
            && stripeCartRef.current === requestSignature
        ) {
            setError("");
            setProceedToCheckout(true);
            openStripeCheckout(uuid);
            return;
        }

        if (totalTicketQuantity > 0 && !isSubmitting) {
            setIsSubmitting(true);
            setCheckout(false);
            setError("");
            setErrorList([]);
            setSuccess(false);
            setPromoCodeMessage("");
            resetCinemaFees();

            const requestUrl = `${API_BASE_URL}/ticket`;

            const data = {
                ...request,
                user_session_id: null,
            };

            if (bookingSessionId) {
                data.user_session_id = bookingSessionId;
            }
            axios
                .post(requestUrl, data)
                .then(function(response) {
                    if (response.status === 201) {
                        // The hold now matches this request, so a reopen can reuse it.
                        stripeCartRef.current = requestSignature;
                        setHoldVersion((version) => version + 1);
                        setBookingSessionId(response.data.data.user_session_id);
                        setUuid(response.data.data.uuid);
                        setAppliedPromo(response.data.data.promo);
                        setCheckout(false);
                        setSuccess(true);

                        if (promoCode && response.data.data.promo.applied) {
                            setPromoCodeSuccess(true);
                            setPromoCodeMessage("Tickets added successfully");
                        } else {
                            setPromoCodeSuccess(null);
                            setError(response.data.data.message);
                            setPromoCodeMessage(response.data.message);
                        }

                        if (response.data.data.referral_applied) {
                            setReferralSuccess(true);
                        } else {
                            setReferralSuccess(null);
                            setError(response.data.data.message);
                        }

                        if (response.data.data.payment) {
                            const hasPaymentUrl = response.data.data.payment.action_url;
                            const isNormalUser = user?.account_type_id === ACCOUNT_TYPE_NORMAL || response?.data?.data?.userType === ACCOUNT_TYPE_NORMAL;
                            const isPaid = response?.data?.data?.total_price_cents > 0;
                            // Stripe groups have no Eway action_url, so don't require one to
                            // reach the terms drawer and, from there, the Stripe checkout.
                            const isStripe = getStripeEnabled(whiteLabel);
                            if (isPaid && isNormalUser && (hasPaymentUrl || isStripe)) {
                                if (hasPaymentUrl?.includes("Email=temp-user")) {
                                    setError("Your session has been timed out, for security reasons please refresh the page");
                                    setUserAuth(() => ({
                                        user: null,
                                        token: null,
                                        isLoggedIn: false,
                                    }));
                                } else {
                                    setCheckout(false);
                                    setPendingActionUrl(hasPaymentUrl);

                                    if (isStripe && isVoucherCheckout) {
                                        // VM/seasonal collect terms in the summary; no terms drawer here.
                                        setProceedToCheckout(true);
                                        openStripeCheckout(response.data.data.uuid);
                                    } else {
                                        openDrawer("termsDrawer");

                                        // triggered by logged in gifting page/ vm page
                                        if (!isStripe && isVoucherCheckout) {
                                            setSelectedItems([]);
                                            setProceedToCheckout(false);
                                            window.open(hasPaymentUrl, "_self");
                                            setTimeout(() => setTermsAccepted(false), 4000);
                                        }
                                    }
                                }
                            } else {
                                setCheckout(true);
                                setEwayAccessCode(response.data.data.payment.access_code);
                                setEwayFormActionUrl(response.data.data.payment.action_url);

                                // determine if complimentary VM
                                if (!isPaid && isNormalUser) {
                                    if (isVoucherManager || seasonalPage === true) {
                                        const displayPrice = whiteLabel?.subscriber_group?.display_price;
                                        if (displayPrice === SITE_DISPLAY_COMPLIMENTARY) {
                                            checkoutComplementary(response.data.data.uuid);
                                            setTimeout(() => setTermsAccepted(false), 4000);
                                        }
                                    } else if (whiteLabel?.enable_agentic_ai === true) {
                                        setPendingActionUrl(hasPaymentUrl ?? "");
                                        openDrawer("termsDrawer");
                                    } else {
                                        closeDrawer("termsDrawer");
                                        openDrawer("complimentaryCheckoutDrawer");
                                    }
                                }
                            }
                        }
                    } else {
                        const errorDescs = Array.isArray(response.data.errors)
                            ? response.data.errors.map((err) => err.desc).filter(Boolean)
                            : [];

                        if (errorDescs.length > 1) {
                            // Several items unavailable: show each item's message as a list.
                            setError("Sorry, the following items could not be added:");
                            setErrorList(errorDescs);
                        } else {
                            setError(errorDescs[0] ?? "Fail to add ticket(s), Please try again");
                            setErrorList([]);
                        }
                        setPromoCodeMessage(response.data.message);
                    }
                })
                .then(() => {
                    setIsSubmitting(false);
                })
                .catch((error) => {
                    setAppliedPromo(null);
                    if (isLoggedIn && error?.response?.status === 401) {
                        window.location = "/";
                    }

                    setIsSubmitting(false);
                    // `errors` is often an empty array on a 400, which used to swallow the message.
                    const responseData = error?.response?.data;
                    const errorDescs = Array.isArray(responseData?.errors)
                        ? responseData.errors.map((err) => err.desc).filter(Boolean)
                        : [];
                    if (errorDescs.length > 1) {
                        setError("Sorry, the following items could not be added:");
                        setErrorList(errorDescs);
                    } else {
                        setError(
                            errorDescs[0]
                            ?? responseData?.message
                            ?? "Could not complete checkout, please try again."
                        );
                        setErrorList([]);
                    }
                    setSuccess(false);
                    if (error?.response?.data?.errors?.type === "promo_code") {
                        setPromoCodeSuccess(false);
                        setError(error?.response?.data?.message);
                    }
                    if (error?.response?.data?.errors?.type === "referral_code") {
                        setReferralSuccess(false);
                        setError(error?.response?.data?.message);
                    }
                    if (error?.response?.data?.data?.user_session_id) {
                        // Set the booking session Id for attempted booking otherwise system considers any changes from now to be a different purchase,
                        // and any held vouchers wont be released
                        setBookingSessionId(error?.response?.data?.data?.user_session_id);
                    }
                });
        } else {
            setPromoCodeMessage("");
            setError("");
        }
    };

    const fetchReInitialiseEwayTicket = (uuid) => {
        setEwayFormActionUrl(null);
        axios
            .get(`${API_BASE_URL}/ticket/re-initialise-eway-ticket/${uuid}`)
            .then(function(response) {
                const actionUrl = response.data?.data?.payment?.action_url;
                if (actionUrl) {
                    window.open(actionUrl, "_self");
                } else {
                    checkoutComplementary(uuid);
                }
            })
            .catch((error) => {
                setError(error);
            });
    };

    const [cinemaBookingStatus, setCinemaBookingStatus] = useState(0);
    const [cinemaBookingFee, setCinemaBookingFee] = useState(null);
    const [cinemaUpgradeFee, setCinemaUpgradeFee] = useState(null);
    const [isPolling, setIsPolling] = useState(false);

    // Fees quoted for the previous cart are wrong once the hold changes, so drop them
    // and let the drawer ask the bot to re-quote.
    const resetCinemaFees = () => {
        setCinemaBookingStatus(0);
        setCinemaBookingFee(null);
        setCinemaUpgradeFee(null);
    };

    const postPurchase = (uuid) => {
        if (!uuid) return;

        return axios.get(`${API_BASE_URL}/ticket/success_booking_with_cinema/${uuid}`)
            .then(response => {
                return response.data;
            })
            .catch(error => {
                console.error("Error calling postPurchase:", error);
                setError("Failed to process booking with cinema");
                throw error;
            });
    };

    const checkBookWithCinemaStatus = (purchaseId) => {
        if (!purchaseId) return;

        setIsPolling(true);

        const startTime = Date.now();
        const pollInterval = 5000; // Poll every 5 seconds
        const maxPollTime = 180000; // Poll for 3 minutes max

        const pollStatus = () => {
            axios.get(`${API_BASE_URL}/fetch_cinema_fees?purchase_id=${purchaseId}`)
                .then(response => {
                    const book_cinema_status = response.data.data.book_with_cinema_status;
                    setCinemaBookingStatus(book_cinema_status);

                    // Always update the fees, they'll only be shown when status is 1
                    setCinemaBookingFee(response.data.data.cinema_booking_fee);
                    setCinemaUpgradeFee(response.data.data.cinema_upgrade_fee);

                    if (book_cinema_status === 1) {
                        // Status is 1, we can stop polling
                        setIsPolling(false);
                    } else if (Date.now() - startTime < maxPollTime) {
                        // Continue polling if we haven't reached the max time
                        setTimeout(pollStatus, pollInterval);
                    } else {
                        // Max poll time reached
                        setIsPolling(false);
                    }
                })
                .catch(error => {
                    console.error("Error checking book with cinema status:", error);
                    setIsPolling(false);
                });
        };

        pollStatus();
    };

    // Creates/refreshes a Stripe Embedded Checkout session and stores the client_secret
    // so StripeCheckoutDrawer can mount. Returns the secret (or null) to the caller.
    const initialiseStripeSession = (uuid, isVoucherTicket = false) => {
        const path = isVoucherTicket ? "initialise-stripe-ticket" : "initialise-stripe";
        setStripeClientSecret(null);
        setStripeSessionId(null);
        return axios
            .get(`${API_BASE_URL}/ticket/${path}/${uuid}`)
            .then((response) => {
                const payment = response.data?.data?.payment;
                const clientSecret = payment?.client_secret ?? null;
                setStripeSessionId(payment?.session_id ?? null);
                setStripeClientSecret(clientSecret);
                return clientSecret;
            })
            .catch((error) => {
                // AlertMessage renders this, so keep it a string rather than the axios error.
                setError(error?.response?.data?.message ?? "Could not start checkout, please try again");
                return null;
            });
    };

    const fetchInitialiseStripe = (uuid) => initialiseStripeSession(uuid, false);
    const fetchInitialiseStripeTicket = (uuid) => initialiseStripeSession(uuid, true);

    // VM/seasonal: create the Stripe session for a held purchase and open its drawer.
    const openStripeCheckout = (purchaseUuid) => {
        setIsStripeCheckoutLoading(true);

        return initialiseStripeSession(purchaseUuid)
            .then((clientSecret) => {
                if (clientSecret) {
                    openDrawer("stripeCheckoutDrawer");
                    return clientSecret;
                }

                setProceedToCheckout(false);
                if (purchaseUuid) {
                    checkoutComplementary(purchaseUuid);
                }
                return null;
            })
            .finally(() => {
                setIsStripeCheckoutLoading(false);
            });
    };

    // Polls booking status after checkout is closed, then redirects on success.
    const pollPaymentStatus = (purchaseUuid) => {
        if (!purchaseUuid) return Promise.resolve(false);

        const deadline = Date.now() + 30000;
        const interval = 3000;
        setIsConfirmingPayment(true);

        const check = () => axios
            .get(`${API_BASE_URL}/ticket/payment-status/${purchaseUuid}`)
            .then((response) => {
                const data = response.data?.data;
                if (data?.succeeded) {
                    const path = data.is_voucher_to_ticket
                        ? `/success_book_with_cinema?id=${purchaseUuid}`
                        : `/ticket/success/${purchaseUuid}`;
                    window.open(path, "_self");
                    return true;
                }
                if (!data?.pending) return false;

                if (Date.now() >= deadline) return false;
                return new Promise((resolve) => {
                    setTimeout(resolve, interval);
                }).then(check);
            })
            .catch(() => false);

        return check().then((succeeded) => {
            if (!succeeded) {
                setIsConfirmingPayment(false);
            }
            return succeeded;
        });
    };

    const fetchReInitialiseEway = (uuid) => {
        setEwayFormActionUrl(null);
        axios
            .get(`${API_BASE_URL}/ticket/re-initialise-eway/${uuid}`)
            .then(function(response) {
                if (response.data.data.payment) {
                    // triggered by VM and gifting fast login/signup
                    if (isVoucherManager && response.data.data.userType === ACCOUNT_TYPE_NORMAL || user?.account_type_id === ACCOUNT_TYPE_NORMAL && seasonalPage) {
                        setProceedToCheckout(false);
                        setSelectedItems([]);
                        window.open(response.data.data.payment.action_url, "_self");
                        setTimeout(() => setTermsAccepted(false), 4000);
                    } else {
                        setEwayAccessCode(response.data.data.payment.access_code);
                        setEwayFormActionUrl(response.data.data.payment.action_url);
                    }
                }
            })
            .catch((error) => {
                if (error?.response?.status === 401 && error?.response?.data?.data?.public_group === 1) {
                    setError("Your session has been timed out, for security reasons please refresh the page");
                    setUserAuth(() => ({
                        user: null,
                        token: null,
                        isLoggedIn: false,
                    }));
                } else if (error?.response?.status === 401 && error?.response?.data?.data?.public_group === 0) {
                    window.location = "/";
                }
            });
    };

    return (
        <BookSessionContext.Provider
            value={{
                bookingSessionId,
                uuid,
                setBookingSessionId,
                isSubmitting,
                checkout,
                setCheckout,
                ticketQuantity,
                setTicketQuantity,
                selectedItems,
                setSelectedItems,
                promoCode,
                setPromoCode,
                promoCodeSuccess,
                setPromoCodeSuccess,
                promoCodeMessage,
                success,
                error,
                setError,
                errorList,
                setErrorList,
                checkoutBookingSession,
                setPromoCodeMessage,
                ewayAccessCode,
                ewayFormActionUrl,
                fetchReInitialiseEway,
                fetchReInitialiseEwayTicket,
                fetchInitialiseStripe,
                fetchInitialiseStripeTicket,
                openStripeCheckout,
                pollPaymentStatus,
                isConfirmingPayment,
                isStripeCheckoutLoading,
                isTempAccount,
                setIsTempAccount,
                referralCode,
                setReferralCode,
                referralSuccess,
                setReferralSuccess,
                termsAccepted,
                setTermsAccepted,
                seasonalPage,
                setSeasonalPage,
                errorMessages,
                setErrorMessages,
                cinemaBookingStatus,
                cinemaBookingFee,
                cinemaUpgradeFee,
                holdVersion,
                isPolling,
                checkBookWithCinemaStatus,
                postPurchase,
                setUuid,
                proceedToCheckout,
                setProceedToCheckout
            }}
        >
            {children}
        </BookSessionContext.Provider>
    );
};

export default BookSessionContextProvider;
